import React, { useCallback, useEffect, useState } from 'react';
import { adminService, AdminPlan } from '../../services/adminApi';
import {
  GlassButton, GlassCard, GlassInput, GlassPanel, GlassSelect, LoadingSpinner,
} from '../../components/GlassUI';
import {
  FaCreditCard, FaCheck, FaTimes, FaExclamationTriangle, FaCrown,
  FaPlus, FaUsers, FaCalendarCheck, FaTrash, FaSave, FaBoxOpen, FaTag,
} from 'react-icons/fa';

const STATUSES = ['TRIAL', 'ACTIVE', 'EXPIRED', 'CANCELLED', 'PAST_DUE'];

const statusChip = (status: string) => {
  const map: Record<string, string> = {
    ACTIVE: 'bg-emerald-100 text-emerald-700',
    TRIAL: 'bg-blue-100 text-blue-700',
    EXPIRED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-slate-200 text-slate-600',
    PAST_DUE: 'bg-orange-100 text-orange-700',
    OWNER: 'bg-amber-100 text-amber-800',
  };
  return map[status] || 'bg-slate-200 text-slate-600';
};

const rupees = (paise: number) => `₹${Math.round((paise || 0) / 100).toLocaleString('en-IN')}`;
const finalRupees = (p: AdminPlan) =>
  `₹${Math.round(((p.price || 0) * (1 - (p.discountPercent || 0) / 100)) / 100).toLocaleString('en-IN')}`;

const emptyPlan = (order: number): AdminPlan => ({
  id: '', name: '', price: 0, discountPercent: 0, durationDays: 365,
  maxCustomers: 200, maxStaff: 1, description: '', features: [], isActive: true,
  isDefault: false, sortOrder: order, badge: '',
});

const AdminSubscriptionPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [featureMeta, setFeatureMeta] = useState<{ id: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [savingPlans, setSavingPlans] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, catalog] = await Promise.all([
        adminService.listSubscriptions(),
        adminService.listPlans(),
      ]);
      setData(subs);
      setPlans(catalog.plans || []);
      setFeatureMeta(catalog.features || []);
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to load subscriptions' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, payload: Record<string, any>, success: string) => {
    setBusyId(id);
    setMsg(null);
    try {
      await adminService.updateSubscription(id, payload);
      setMsg({ type: 'ok', text: success });
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Update failed' });
    } finally {
      setBusyId(null);
    }
  };

  // ── Package catalog editing ──────────────────────────────────────
  const patchPlan = (idx: number, patch: Partial<AdminPlan>) =>
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const toggleFeature = (idx: number, featureId: string) => {
    const p = plans[idx];
    const has = p.features.includes(featureId);
    patchPlan(idx, {
      features: has ? p.features.filter((f) => f !== featureId) : [...p.features, featureId],
    });
  };

  const addPlan = () => setPlans((prev) => [...prev, emptyPlan(prev.length)]);

  const removePlan = (idx: number) => {
    const p = plans[idx];
    if (!window.confirm(`Delete the "${p.name || p.id || 'new'}" package?`)) return;
    setPlans((prev) => prev.filter((_, i) => i !== idx));
  };

  const savePlans = async () => {
    setSavingPlans(true);
    setMsg(null);
    try {
      // Only one package may be the default (free) one.
      let seenDefault = false;
      const payload = plans.map((p) => {
        const isDefault = !!p.isDefault && !seenDefault;
        if (isDefault) seenDefault = true;
        return { ...p, isDefault };
      });
      const res = await adminService.savePlans(payload);
      setPlans(res.plans || payload);
      setMsg({ type: 'ok', text: res.message || 'Packages saved' });
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to save packages' });
    } finally {
      setSavingPlans(false);
    }
  };

  const subs: any[] = data?.subscriptions || [];
  const storeCount: number = data?.storeCustomerCount || 0;
  const owner = subs.find((s) => s.owner?.role === 'ADMIN') || subs[0];
  const ownerExempt = !!owner?.exempt;
  const subscribers = subs.filter((s) => !s.exempt);
  const usagePct =
    owner && !ownerExempt && owner.maxCustomers
      ? Math.min(100, Math.round((storeCount / owner.maxCustomers) * 100))
      : 0;

  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3'>
          <span className='w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center'>
            <FaCreditCard />
          </span>
          Plans & Subscription
        </h1>
        <p className='text-slate-500 mt-1 text-sm'>
          Manage packages, prices & discounts · assign, renew or expire customer accounts
        </p>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-2 text-sm rounded-2xl px-4 py-3 border ${
            msg.type === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.type === 'ok' ? <FaCheck className='mt-0.5 shrink-0' /> : <FaExclamationTriangle className='mt-0.5 shrink-0' />}
          <span className='break-all'>{msg.text}</span>
          <button className='ml-auto text-xs' onClick={() => setMsg(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* ── Owner account (no plan needed) ── */}
          {owner && (
            <div className='rounded-3xl bg-gradient-to-br from-slate-800 to-slate-950 p-6 md:p-7 text-white shadow-lg'>
              <div className='flex flex-wrap items-center gap-3'>
                <FaCrown className='text-amber-400 text-xl' />
                <div className='min-w-0'>
                  <p className='font-bold'>{owner.owner?.name || 'Owner'} · <span className='text-white/70 text-sm'>{owner.owner?.email}</span></p>
                  <p className='text-white/60 text-xs mt-0.5'>
                    {ownerExempt
                      ? 'Super admin / owner account — no plan, no trial, never expires.'
                      : 'Primary account'}
                  </p>
                </div>
                <span className='ml-auto text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'>
                  {ownerExempt ? 'OWNER · UNLIMITED' : 'OWNER'}
                </span>
              </div>
            </div>
          )}

          {/* ── Store usage ── */}
          {owner && !ownerExempt && (
            <GlassPanel className='p-5'>
              <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
                <h3 className='font-bold text-slate-900 flex items-center gap-2'>
                  <FaUsers className='text-amber-500' /> Store usage
                </h3>
                <span className='text-sm text-slate-500'>
                  {storeCount} customers of {owner.maxCustomers} allowed · {usagePct}%
                </span>
              </div>
              <div className='h-3 bg-slate-100 rounded-full overflow-hidden'>
                <div
                  className={`h-full rounded-full transition-all ${
                    usagePct >= 90 ? 'bg-red-500' : usagePct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${Math.max(usagePct, 2)}%` }}
                />
              </div>
            </GlassPanel>
          )}

          {/* ── Package catalog (prices & discounts) ── */}
          <GlassPanel className='p-5'>
            <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
              <h2 className='font-bold text-slate-900 flex items-center gap-2'>
                <FaBoxOpen className='text-indigo-500' /> Packages, Prices & Discounts
              </h2>
              <div className='flex gap-2'>
                <GlassButton variant='secondary' className='!px-3 !py-2 text-xs' onClick={addPlan}>
                  <FaPlus size={10} /> Add package
                </GlassButton>
                <GlassButton variant='gold' className='!px-3 !py-2 text-xs' disabled={savingPlans} onClick={savePlans}>
                  <FaSave size={11} /> {savingPlans ? 'Saving…' : 'Save packages'}
                </GlassButton>
              </div>
            </div>
            <p className='text-xs text-slate-500 mb-4'>
              Type price in ₹ per year, set a discount %, choose what each package unlocks. The customer CRM shows
              these live.
            </p>

            <div className='space-y-4'>
              {plans.map((p, idx) => (
                <div key={idx} className='border border-slate-200 rounded-2xl p-4 bg-slate-50/60'>
                  <div className='flex flex-wrap items-center gap-2 mb-3'>
                    <span className='text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-amber-300'>
                      {p.id || 'NEW'}
                    </span>
                    <span className='text-xs text-slate-500'>Package {idx + 1}</span>
                    <div className='ml-auto flex items-center gap-3'>
                      <label className='flex items-center gap-1.5 text-xs text-slate-600'>
                        <input type='checkbox' checked={p.isActive} onChange={(e) => patchPlan(idx, { isActive: e.target.checked })} />
                        Active
                      </label>
                      <label className='flex items-center gap-1.5 text-xs text-slate-600'>
                        <input type='checkbox' checked={p.isDefault} onChange={(e) => patchPlan(idx, { isDefault: e.target.checked })} />
                        Free / default
                      </label>
                      <button onClick={() => removePlan(idx)} className='text-red-500 hover:text-red-700' title='Delete package'>
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Plan id</label>
                      <GlassInput value={p.id} onChange={(e) => patchPlan(idx, { id: e.target.value.toUpperCase() })} placeholder='BASIC' />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Name</label>
                      <GlassInput value={p.name} onChange={(e) => patchPlan(idx, { name: e.target.value })} placeholder='Basic' />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Price ₹ / year</label>
                      <GlassInput
                        type='number'
                        value={String(Math.round((p.price || 0) / 100))}
                        onChange={(e) => patchPlan(idx, { price: Math.max(0, Math.round(Number(e.target.value) || 0)) * 100 })}
                        placeholder='9999'
                      />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Discount %</label>
                      <GlassInput
                        type='number'
                        value={String(p.discountPercent || 0)}
                        onChange={(e) => patchPlan(idx, { discountPercent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                        placeholder='0'
                      />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Days</label>
                      <GlassInput type='number' value={String(p.durationDays)} onChange={(e) => patchPlan(idx, { durationDays: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Max customers</label>
                      <GlassInput type='number' value={String(p.maxCustomers)} onChange={(e) => patchPlan(idx, { maxCustomers: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Max staff</label>
                      <GlassInput type='number' value={String(p.maxStaff)} onChange={(e) => patchPlan(idx, { maxStaff: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
                    </div>
                    <div>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Badge (optional)</label>
                      <GlassInput value={p.badge || ''} onChange={(e) => patchPlan(idx, { badge: e.target.value })} placeholder='Popular' />
                    </div>
                    <div className='col-span-2 md:col-span-4'>
                      <label className='block text-[11px] font-semibold text-slate-600 mb-1'>Description</label>
                      <GlassInput value={p.description} onChange={(e) => patchPlan(idx, { description: e.target.value })} placeholder='For small jewelry stores' />
                    </div>
                  </div>

                  <div className='mt-3'>
                    <p className='text-[11px] font-semibold text-slate-600 mb-1.5'>Unlocks</p>
                    <div className='flex flex-wrap gap-2'>
                      {featureMeta.map((f) => {
                        const on = p.features.includes(f.id);
                        return (
                          <button
                            key={f.id}
                            type='button'
                            onClick={() => toggleFeature(idx, f.id)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                              on
                                ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                            }`}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <p className='mt-3 text-xs text-slate-500 flex items-center gap-2'>
                    <FaTag className='text-amber-500' />
                    Customer pays <span className='font-bold text-slate-800'>{finalRupees(p)}</span>
                    {p.discountPercent > 0 && (
                      <span className='text-slate-400 line-through'>{rupees(p.price)}</span>
                    )}
                    {p.discountPercent > 0 && (
                      <span className='text-emerald-600 font-semibold'>{p.discountPercent}% off</span>
                    )}
                  </p>
                </div>
              ))}
              {plans.length === 0 && (
                <p className='text-center text-slate-400 py-6 text-sm'>No packages yet — add one.</p>
              )}
            </div>
          </GlassPanel>

          {/* ── Customer accounts ── */}
          <div>
            <h2 className='font-bold text-slate-900 mb-3 flex items-center gap-2'>
              <FaUsers className='text-amber-500' /> Customer accounts ({subscribers.length})
            </h2>
            <div className='grid grid-cols-1 gap-3 md:gap-4'>
              {subscribers.map((s: any) => (
                <GlassPanel key={s.id} className='p-5'>
                  <div className='flex flex-col lg:flex-row lg:items-center gap-4'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <p className='font-bold text-slate-900'>{s.owner?.name || 'Account'}</p>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusChip(s.status)}`}>
                          {s.status}
                        </span>
                      </div>
                      <p className='text-xs text-slate-500'>{s.owner?.email}</p>
                      <p className='text-xs text-slate-400 mt-1'>
                        <FaCalendarCheck className='inline mr-1' />
                        {s.daysLeft !== null && s.daysLeft >= 0 ? `${s.daysLeft} days left` : 'Expired'} ·{' '}
                        {s.endDate
                          ? `ends ${new Date(s.endDate).toLocaleDateString()}`
                          : s.trialEnd
                          ? `trial ends ${new Date(s.trialEnd).toLocaleDateString()}`
                          : 'no end date'}
                      </p>
                    </div>

                    <div className='flex flex-wrap gap-2'>
                      <GlassSelect
                        value={s.plan}
                        disabled={busyId === s.id}
                        onChange={(e) => act(s.id, { plan: e.target.value }, `Plan changed to ${e.target.value}`)}
                      >
                        {plans.filter((p) => p.id).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} · {finalRupees(p)}
                          </option>
                        ))}
                      </GlassSelect>
                      <GlassSelect
                        value={s.status}
                        disabled={busyId === s.id}
                        onChange={(e) => act(s.id, { status: e.target.value }, `Status set to ${e.target.value}`)}
                      >
                        {STATUSES.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </GlassSelect>
                      <GlassButton
                        variant='secondary'
                        className='!px-3 !py-2 text-xs'
                        disabled={busyId === s.id}
                        onClick={() => act(s.id, { extendDays: 30 }, 'Extended by 30 days')}
                      >
                        <FaPlus size={10} /> 30 days
                      </GlassButton>
                      <GlassButton
                        variant='gold'
                        className='!px-3 !py-2 text-xs'
                        disabled={busyId === s.id}
                        onClick={() => act(s.id, { extendDays: 365 }, 'Renewed for 1 full year — ACTIVE')}
                      >
                        <FaCrown size={11} /> Renew yearly
                      </GlassButton>
                    </div>
                  </div>
                </GlassPanel>
              ))}
              {subscribers.length === 0 && (
                <GlassCard className='text-center py-10 text-slate-400'>
                  No customer subscriptions yet — create an account from <b>Accounts &amp; Users</b>.
                </GlassCard>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminSubscriptionPage;
